import {animation} from "@global/global-animation";
import * as Hammer from "hammerjs";
import lazyloader from '@utilities/sal-lazyload/sal-lazyload'
import {track_abstract, tracking_events} from '@utilities/track-abstract/track-abstract';

const DEFAULT_OPTIONS = {
    galleryWrapper: 'global-gallery-slider__wrapper',
    btnNextClass: 'global-gallery-slider__btn-next',
    btnPrevClass: 'global-gallery-slider__btn-prev',
    progressBarClass: 'global-gallery-slider__progress-indicator',
    progressBarDuration: 5000,
    galleryTouchThreshold: 80,
    category: 'gallery_slider',
};

const globalGallerySlider = function(rootElement, _options) {
    if (!rootElement) {
        return;
    }

    let options = {...DEFAULT_OPTIONS, ..._options}
    let autostepping = null
    let currentSlideIndex = 1 // Human readable slide index
    let isInTransition = false
    const wrapper = rootElement.querySelector(`.${options.galleryWrapper}`)
    // Init slides.
    // To allow seamless wraparound, we need to clone the first and last slides.
    const slides = Array.from(wrapper.children)
    const firstSlide = slides[0]
    const lastSlide = slides[slides.length - 1]
    const firstSlideClone = firstSlide.cloneNode(true)
    const lastSlideClone = lastSlide.cloneNode(true)
    wrapper.appendChild(firstSlideClone)
    wrapper.insertBefore(lastSlideClone, firstSlide)
    slides.push(firstSlideClone)
    slides.unshift(lastSlideClone)
    slides[2].classList.add('global-gallery-slider__item--next');

    // Make sure to load all images and not only once we slide there
    wrapper.querySelectorAll('.sal-image.preview').forEach(image => {
        lazyloader.lazyloadPicture(image);
    })

    // Init
    if (rootElement.dataset.interval) {
        const interval = parseInt(rootElement.dataset.interval);
        if (interval > 0) {
            options.progressBarDuration = interval
        } else {
            autostepping = false;
        }
    }

    if (rootElement.dataset.category) {
        options.category = rootElement.dataset.category;
    }

    const calculateLeft = (index) => {
        let left = 0;
        for (let i = 0; i < index; i++) {
            left -= slides[i].offsetWidth;
        }
        left += (wrapper.clientWidth / 2)
        left -= slides[index].offsetWidth / 2;

        return left;
    }

    const track = (type) => {
        track_abstract.trigger_event(type === 'auto' ? tracking_events.global_gallery_slider_navigate_auto : tracking_events.global_gallery_slider_navigate, {
            category: options.category,
            title: slides[currentSlideIndex].dataset.title || 'Slide ' + currentSlideIndex
        });
    }

    const next = () => {
        stopAutoStepping();
        switchSlide(currentSlideIndex + 1);
        track('next');
    }
    const prev = () => {
        stopAutoStepping();
        switchSlide(currentSlideIndex - 1);
        track('prev');
    }

    /**
     * currentSlideIndex is 1-based
     */
    const switchSlide = (targetIndex) => {
        if (isInTransition) {
            return;
        }
        isInTransition = true;

        // Clamp targetIndex within a valid range from
        let nextIndex = (slides.length + targetIndex) % slides.length;

        // Special case when sliding to the last slide as we actually need to slide from the first slide to the second slide or else we see white :(
        if (currentSlideIndex === slides.length - 2 && nextIndex === slides.length - 1) {
            nextIndex = 1;
            wrapper.style.left = calculateLeft(0) + 'px';
        }

        let nextUserfacingIndex = nextIndex === 0 ? slides.length - 2 : nextIndex === slides.length - 1 ? 1 : nextIndex;
        const newLeftOffset = calculateLeft(nextIndex) + 'px';
        currentSlideIndex = nextUserfacingIndex;

        slides[nextIndex].classList.remove('global-gallery-slider__item--next');
        slides[nextIndex + 1].classList.add('global-gallery-slider__item--next');
        wrapper.style.setProperty('--animate_global_gallery_slider_from', wrapper.style.left);
        wrapper.style.setProperty('--animate_global_gallery_slider_to', newLeftOffset);
        animation.addAnimation(wrapper, 'animate_global_gallery_slider', '0.5s', [{
            eventType: 'animationend',
            eventCallback: () => {
                wrapper.style.left = calculateLeft(nextUserfacingIndex) + 'px';
                isInTransition = false;
            }
        }]);

        setTimeout(() => {
            updatePageIndicator()
            startAutoNextStep();
        }, 1);
    }

    const updatePageIndicator = () => {
        // Page number
        const count = slides.length;
        rootElement.querySelector(".page-indicator").innerHTML = `${currentSlideIndex}/${count - 2}`;

        // mobile bullet-indicators
        const indicatorWrapper = rootElement.querySelector('.gallery__indicator');
        if (indicatorWrapper) {
            const activeIndicator = rootElement.querySelector('.indicator--active');
            if (activeIndicator) {
                activeIndicator.classList.remove('indicator--active');
            }

            const newActiveIndicator = rootElement.querySelector(`.gallery__indicator .indicator:nth-child(${currentSlideIndex})`);
            newActiveIndicator.classList.add('indicator--active');
        }
    }

    const createIndicator = (numberOfIndicators) => {
        let i = 0;
        let indicatorWrapper = document.createElement('div');
        indicatorWrapper.classList.add('gallery__indicator');
        let indicator = document.createElement('span');
        indicator.classList.add('indicator');

        while (i < numberOfIndicators) {
            indicatorWrapper.appendChild(indicator.cloneNode(true));
            i++;
        }

        indicatorWrapper.firstChild.classList.add('indicator--active');

        return indicatorWrapper;
    }

    const startAutoNextStep = () => {
        if (autostepping !== false) {
            const progressBar = rootElement.querySelector(`.${options.progressBarClass}`);
            if (progressBar) {
                progressBar.style.width = 0;
                const animationEndFn = () => {
                    progressBar.style.width = 0;
                    if (autostepping !== false) {
                        switchSlide(currentSlideIndex + 1);
                        track('auto');
                    }
                };
                if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
                    progressBar.style.visibility = 'hidden';
                    // Reduced motion means that animate.js forces the dduration to 1ms, thus the progress bar cannot be used.
                    // Crappy library if "prefer" "reduced" means "no animation at all" ;-)
                    setTimeout(animationEndFn, options.progressBarDuration);
                }
                else {
                    animation.addAnimation(progressBar, 'animate_global_gallery_progress', options.progressBarDuration + "ms", [{
                        eventType: 'animationend',
                        eventCallback: animationEndFn
                    }]);
                }
            }
        }
    }

    const stopAutoStepping = () => {
        const progressBar = rootElement.querySelector(`.${options.progressBarClass}`);

        autostepping = false;
        if (progressBar) {
            progressBar.style.display = 'none';
        }
    }

    // Init and update Pagination
    wrapper.style.left = calculateLeft(1) + 'px';
    rootElement.querySelector(`.${options.btnNextClass}`).addEventListener('click', next);
    rootElement.querySelector(`.${options.btnPrevClass}`).addEventListener('click', prev);
    updatePageIndicator();

    // Start autostepping once the gallery is abotut to become visible
    let observerOptions = {
        root: null,
        rootMargin: '0px',
        threshold: 0.0
    };
    const observerCallback = () => {
        if (autostepping === null) {
            startAutoNextStep();
        }
    };
    const observer = new IntersectionObserver(observerCallback, observerOptions);
    observer.observe(rootElement);

    const numberIndicators = wrapper.childNodes.length - 2;
    rootElement.querySelector('.gallery__controls').parentNode.append(createIndicator(numberIndicators));

    // Add swipe support
    const hammerInstance = new Hammer(wrapper);
    hammerInstance.get('swipe').set({
        direction: Hammer.DIRECTION_HORIZONTAL,
        threshold: options.galleryTouchThreshold
    });
    hammerInstance.on('swipeleft', next);
    hammerInstance.on('swiperight', prev);

    // Reposition the slide on browser resize (because we all know this happens during QS, but not during real usage ;-))
    let resizeTimer;
    window.addEventListener('resize', () => {
        // debounce resize
        if (resizeTimer) clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
            wrapper.style.left = calculateLeft(currentSlideIndex) + 'px';
        }, 100);
    });

    // Add Click Listener for slides
    slides.forEach((slide, index) => {
        // a: For "Slide to angeschnittene slide"
        slide.addEventListener('click', () => {
            if (currentSlideIndex !== index) {
                stopAutoStepping();
                switchSlide(index);
                track('click');
            }
        });

        // b: For Analytics
        slide.querySelectorAll('*[data-track="true"]').forEach((element) => {
            element.addEventListener('click', () => {
                track_abstract.trigger_event(tracking_events.global_gallery_slider_click, {
                    category: options.category,
                    title: slide.dataset.title || 'Slide ' + index
                });
            });
        });
    })
};

export {globalGallerySlider};
